import React, { useEffect, useState } from 'react';
import { TrendingDown, PartyPopper, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { formatTHB } from '@/lib/pricing';
import { numberLocale } from '@/lib/format';
import { formatPct } from '@/lib/scooterDiscounts';
import { grandTotal } from '@/lib/deliveryFees';

/**
 * DiscountBanner — displayed on the product page ABOVE the booking form.
 * Shows the discounts that really apply to this scooter, as described by the
 * server (`service.duration_discounts`): fixed prices per day or percentages.
 * Renders nothing when there is none.
 */
export const DiscountBanner = ({ basePrice, offer }) => {
  const { t } = useTranslation();
  if (!offer || offer.type === 'none' || !offer.tiers || !offer.tiers.length) return null;
  const isPct = offer.type === 'percentage';
  return (
    <div className="rounded-2xl border border-accent/30 bg-accent/5 p-4" data-testid="discount-banner" data-type={offer.type}>
      <div className="flex items-center gap-2 text-sm font-semibold text-accent mb-2">
        <TrendingDown className="w-4 h-4" />
        {t('scooters.priceSummary.longDurationBanner')}
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-light">{t('scooters.priceSummary.fromOneDay')}</span>
          <span className="font-semibold text-primary">{t('scooters.priceSummary.pricePerDay', { price: formatTHB(basePrice) })}</span>
        </div>
        {offer.tiers.map((tier) => (
          <div key={tier.min_days} className="flex items-center justify-between text-sm" data-testid={`tier-row-${tier.min_days}`}>
            <span className="text-ink-light">{t('scooters.priceSummary.fromDays', { days: tier.min_days })}</span>
            <span className="font-semibold text-accent">
              {isPct
                ? t('scooterPricing.offer.percentOnly', { pct: formatPct(tier.discount_percentage, numberLocale()) })
                : t('scooters.priceSummary.pricePerDay', { price: formatTHB(tier.daily_price) })}
            </span>
          </div>
        ))}
      </div>
      {isPct && <p className="text-[11px] text-ink-muted mt-2">{t('scooterPricing.offer.notCumulative')}</p>}
    </div>
  );
};

/**
 * PriceSummary — pricing panel of the booking dialog. Displays ONLY the server
 * quote for the selected dates (useScooterQuote): days, subtotal, the one
 * discount applied, total. While loading or after an error no amount is shown.
 * `delivery` ({ zone, fee }): a scooter delivery chosen by zone — the rental,
 * the delivery + collection and the total are then shown on separate lines.
 */
export const PriceSummary = ({ quote, delivery = null }) => {
  const { t } = useTranslation();
  const [pulse, setPulse] = useState(false);
  const [lastTierKey, setLastTierKey] = useState(null);
  const s = quote?.status === 'ok' ? quote.summary : null;
  const tierKey = s && s.discount_amount > 0 ? `${s.discount_min_days}` : null;
  useEffect(() => {
    if (tierKey && tierKey !== lastTierKey && lastTierKey !== null) {
      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 1800);
      return () => clearTimeout(timer);
    }
    setLastTierKey(tierKey);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierKey]);

  if (!quote || quote.status === 'idle') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="price-summary">
        <p className="text-sm text-ink-muted">{t('scooters.priceSummary.selectDatesHint')}</p>
      </div>
    );
  }
  if (quote.status === 'loading') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="price-summary">
        <p className="text-sm text-ink-muted flex items-center gap-2" data-testid="price-summary-loading">
          <Loader2 className="w-4 h-4 animate-spin" />{t('scooterPricing.summary.calculating')}
        </p>
      </div>
    );
  }
  if (quote.status === 'error' || !s) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="price-summary">
        <p className="text-sm text-red-700 flex items-start gap-2" data-testid="price-summary-error">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{t('scooterPricing.summary.error')}
        </p>
        <button type="button" onClick={quote.retry} className="mt-2 text-xs font-semibold text-accent underline" data-testid="price-summary-retry">
          {t('scooterPricing.summary.retry')}
        </button>
      </div>
    );
  }

  const hasDiscount = s.discount_amount > 0;
  const pctLine = s.discount_percentage != null;
  const next = s.next_discount;
  return (
    <div className={`rounded-2xl border p-4 transition-all ${pulse ? 'border-accent bg-accent/10 shadow-lg' : 'border-slate-200 bg-white'}`} data-testid="price-summary">
      <p className="text-xs text-ink-light mb-1" data-testid="price-days">{t('scooterPricing.summary.rentalDays', { count: s.days })}</p>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-ink-light">{t('scooterPricing.summary.subtotal')}</span>
        <span className="text-sm font-semibold text-primary" data-testid="price-subtotal">{formatTHB(s.subtotal)}</span>
      </div>
      {hasDiscount && (
        <div className="flex items-center justify-between text-sm mb-2 text-green-700" data-testid="price-savings-row">
          <span>
            {pctLine
              ? t('scooterPricing.summary.percentDiscount', { pct: formatPct(s.discount_percentage, numberLocale()), days: s.discount_min_days })
              : t('scooterPricing.summary.fixedDiscount', { days: s.fixed_tier?.min_days, price: formatTHB(s.fixed_tier?.daily_price) })}
          </span>
          <span className="font-semibold" data-testid="price-savings">−{formatTHB(s.discount_amount)}</span>
        </div>
      )}
      {delivery ? (
        <>
          <div className="border-t border-slate-100 pt-2 flex items-center justify-between text-sm" data-testid="price-rental">
            <span className="text-ink-light">{t('deliveryFees.rental')}</span>
            <span className="font-semibold text-primary">{formatTHB(s.total)}</span>
          </div>
          <div className="flex items-center justify-between text-sm mt-1" data-testid="price-delivery">
            <span className="text-ink-light">{t('deliveryFees.feeInZone', { zone: delivery.zone })}</span>
            <span className="font-semibold text-primary">{formatTHB(delivery.fee)}</span>
          </div>
          <div className="border-t border-slate-100 pt-2 mt-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-primary">{t('deliveryFees.total')}</span>
            <span className="text-2xl font-bold text-primary" data-testid="price-total">{formatTHB(grandTotal(s.total, delivery.fee))}</span>
          </div>
        </>
      ) : (
        <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-primary">{t('scooters.priceSummary.total')}</span>
          <span className="text-2xl font-bold text-primary" data-testid="price-total">{formatTHB(s.total)}</span>
        </div>
      )}
      {hasDiscount && (
        <div className={`mt-3 rounded-xl px-3 py-2.5 flex items-start gap-2 ${pulse ? 'animate-pulse' : ''} bg-green-50 border border-green-200`} data-testid="discount-active-message">
          <PartyPopper className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed">
            <p className="font-semibold text-green-800">{t('scooters.priceSummary.discountAppliedTitle')}</p>
            <p className="text-green-700">
              <Trans
                i18nKey="scooters.priceSummary.discountAppliedBody"
                values={{ savings: formatTHB(s.discount_amount) }}
                components={{ strong: <span className="font-bold" /> }}
              />
            </p>
          </div>
        </div>
      )}
      {pulse && (
        <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-accent" data-testid="unlock-animation">
          <Sparkles className="w-3.5 h-3.5" />
          {t('scooters.priceSummary.unlockedAnimation')}
        </div>
      )}
      {next && next.days_needed > 0 && next.days_needed <= 3 && (
        <div className="mt-3 rounded-xl px-3 py-2.5 flex items-start gap-2 bg-amber-50 border border-amber-200" data-testid="close-to-tier-hint">
          <TrendingDown className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed font-semibold text-amber-800">
            {next.discount_percentage != null
              ? t('scooterPricing.summary.closeToPercent', { count: next.days_needed, pct: formatPct(next.discount_percentage, numberLocale()) })
              : t('scooters.priceSummary.closeToUnlock', {
                count: next.days_needed,
                kind: next.min_days >= 30 ? t('scooters.priceSummary.kindMonthly') : t('scooters.priceSummary.kindLongTerm'),
                saving: formatTHB(next.potential_saving),
              })}
          </p>
        </div>
      )}
    </div>
  );
};
